import { create } from 'zustand';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';
import type { Call } from '../types/dashboard';

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface HierarchicalCallsStore {
  calls: Call[];
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  
  // Actions
  fetchCalls: (page?: number, search?: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  clearCalls: () => void;
}

const initialPagination: PaginationState = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  hasNext: false,
  hasPrev: false,
};

export const useHierarchicalCallsStore = create<HierarchicalCallsStore>((set, get) => ({
  calls: [],
  pagination: initialPagination,
  isLoading: false,
  error: null,
  searchQuery: '',

  fetchCalls: async (page = 1, search) => {
    const state = get();
    const searchQuery = search !== undefined ? search : state.searchQuery;
    const perPage = 5;
    
    // Get current user ID from token
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    // Prevent multiple simultaneous requests
    if (state.isLoading) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${getApiBaseUrl()}/api/v1/calls/hierarchical?page=${page}&limit=${perPage}&search=${searchQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { calls: callsData, pagination } = response.data;
      
      // Transform calls data
      const transformedCalls = callsData.map((call: any) => ({
        id: call.id,
        rep: call.representative,
        repAvatar: call.representative.split(' ').map((n: string) => n[0]).join(''),
        representative_id: call.representative_id,
        customer: call.customer_name,
        date: new Date(call.timestamp).toLocaleDateString(),
        time: new Date(call.timestamp).toLocaleTimeString(),
        duration: call.call_duration || '0:00',
        score: call.overall_score || call.total_score || 0,
        status: 'completed'
      }));

      set({ 
        calls: transformedCalls,
        pagination: {
          currentPage: pagination.current_page,
          totalPages: pagination.total_pages,
          totalCount: pagination.total_count,
          hasNext: pagination.has_next,
          hasPrev: pagination.has_prev,
        },
        isLoading: false, 
        error: null,
        searchQuery
      });

    } catch (error) {
      console.error('Error fetching hierarchical calls:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch calls',
        isLoading: false 
      });
    }
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  clearCalls: () => {
    set({ 
      calls: [], 
      pagination: initialPagination,
      isLoading: false, 
      error: null,
      searchQuery: ''
    });
  }
}));
